/* =========================================================
   sports&deco — Calculs sur une activité
   Une "activity" = { sourceFormat, name, sportRaw, samples: [{lat,lon,ele,time}] }
   ========================================================= */

const EARTH_R = 6371000; // m

function haversine(a, b) {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_R * Math.asin(Math.sqrt(h));
}

function classifySport(raw) {
  if (!raw) return 'unknown';
  const r = String(raw).toLowerCase();
  if (r.includes('run') || r.includes('course')) return 'running';
  if (r.includes('ride') || r.includes('cycl') || r.includes('bike') || r.includes('vélo') || r.includes('velo')) return 'cycling';
  if (r.includes('hike') || r.includes('walk') || r.includes('marche')) return 'hiking';
  if (r.includes('swim') || r.includes('nat')) return 'swimming';
  if (r.includes('ski')) return 'ski';
  return 'other';
}

function computeStats(activity) {
  const s = activity.samples;
  if (!s || s.length < 2) {
    return { distanceM: 0, elevGainM: 0, elevLossM: 0, durationS: 0, startTime: null, endTime: null, sport: classifySport(activity.sportRaw), bbox: null };
  }

  let distanceM = 0;
  for (let i = 1; i < s.length; i++) distanceM += haversine(s[i - 1], s[i]);

  let elevGainM = 0, elevLossM = 0;
  const eleSeries = s.map((p) => p.ele).filter((v) => Number.isFinite(v));
  if (eleSeries.length > 1) {
    // léger lissage par moyenne glissante (fenêtre 5) pour limiter le bruit GPS
    const win = 5;
    const smooth = eleSeries.map((_, i) => {
      const start = Math.max(0, i - Math.floor(win / 2));
      const end = Math.min(eleSeries.length, i + Math.floor(win / 2) + 1);
      const slice = eleSeries.slice(start, end);
      return slice.reduce((a, b) => a + b, 0) / slice.length;
    });
    for (let i = 1; i < smooth.length; i++) {
      const d = smooth[i] - smooth[i - 1];
      if (d > 0) elevGainM += d; else elevLossM -= d;
    }
  }

  const startTime = s[0].time && !isNaN(s[0].time) ? s[0].time : null;
  const endTime = s[s.length - 1].time && !isNaN(s[s.length - 1].time) ? s[s.length - 1].time : null;
  const durationS = startTime && endTime ? Math.max(0, (endTime - startTime) / 1000) : 0;

  let minLat = Infinity, maxLat = -Infinity, minLon = Infinity, maxLon = -Infinity;
  for (const p of s) {
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lon < minLon) minLon = p.lon;
    if (p.lon > maxLon) maxLon = p.lon;
  }

  return {
    distanceM,
    elevGainM,
    elevLossM,
    durationS,
    startTime,
    endTime,
    sport: classifySport(activity.sportRaw),
    bbox: { minLat, maxLat, minLon, maxLon },
  };
}

function formatDistance(m, lang = 'fr') {
  const km = m / 1000;
  if (km < 10) return `${km.toFixed(2).replace('.', lang === 'fr' ? ',' : '.')} km`;
  return `${km.toFixed(1).replace('.', lang === 'fr' ? ',' : '.')} km`;
}

function formatElevation(m) {
  return `${Math.round(m)} m`;
}

function formatDuration(s) {
  if (!s) return '—';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  if (h > 0) return `${h}h${String(m).padStart(2, '0')}`;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

function formatPace(distanceM, durationS, sport, lang = 'fr') {
  if (!distanceM || !durationS) return '—';
  if (sport === 'cycling' || sport === 'ski') {
    const kmh = (distanceM / 1000) / (durationS / 3600);
    const v = kmh.toFixed(1).replace('.', lang === 'fr' ? ',' : '.');
    return `${v} km/h`;
  }
  // running / hiking → allure min/km
  const secPerKm = durationS / (distanceM / 1000);
  const m = Math.floor(secPerKm / 60);
  const s = Math.round(secPerKm % 60);
  return `${m}:${String(s).padStart(2, '0')} /km`;
}

function formatDate(d, lang = 'fr') {
  if (!d) return '—';
  return new Intl.DateTimeFormat(lang === 'fr' ? 'fr-FR' : 'en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d);
}

function statValueFor(key, stats, lang) {
  switch (key) {
    case 'distance':  return formatDistance(stats.distanceM, lang);
    case 'elevation': return formatElevation(stats.elevGainM);
    case 'duration':  return formatDuration(stats.durationS);
    case 'pace':      return formatPace(stats.distanceM, stats.durationS, stats.sport, lang);
    case 'date':      return formatDate(stats.startTime, lang);
    default: return '—';
  }
}

function statLabelFor(key, sport) {
  if (key === 'pace') {
    return (sport === 'cycling' || sport === 'ski')
      ? I18N.t('stat.speed.label')
      : I18N.t('stat.pace.label');
  }
  return I18N.t(`stat.${key}.label`);
}
